import { useMemo } from "react";
import { ParentTree, SegmentNode, UseFilteredAudiencesParams } from "./segment-interface";

export const useFilteredAudiences = (
  parseDataFolderDropdown: any,
  watch: any,
  filters: UseFilteredAudiencesParams
) => {
  const { status, brand, createdBy, lastEditedBy } = filters;

  const rawAudienceTree = useMemo(() => {
    if (!Array.isArray(parseDataFolderDropdown)) return [];
    return parseDataFolderDropdown as ParentTree[];
  }, [parseDataFolderDropdown]);

  const flatAudienceList = useMemo(() => {
    function flattenSegments(
      nodes: SegmentNode[] | undefined,
      parentCountry: string
    ): SegmentNode[] {
      if (!nodes) return [];
      return nodes.flatMap((node) => {
        const nodeWithCountry = { ...node, country: parentCountry };

        return node.type === "segment"
          ? [nodeWithCountry]
          : flattenSegments(node.children, parentCountry);
      });
    }

    return rawAudienceTree.flatMap((folder1) => flattenSegments(folder1.children, folder1.folder1));
  }, [rawAudienceTree]);

  const getUniqueOptions = (key: keyof SegmentNode): string[] => {
    const unique = new Set<string>();
    flatAudienceList.forEach((item) => {
      if (item[key]) {
        unique.add(item[key] as string);
      }
    });
    return ["All", ...Array.from(unique).sort()];
  };

  const statusOptions = getUniqueOptions("status");
  const brandOptions = getUniqueOptions("brand");
  const createdByOptions = getUniqueOptions("createdby");
  const lastEditedByOptions = getUniqueOptions("last_modified_by");

  const inputNameValue = watch("search");

  const filteredData = useMemo(() => {
    if (
      !inputNameValue.trim() &&
      brand.includes("All") &&
      status.includes("All") &&
      createdBy.includes("All") &&
      lastEditedBy.includes("All")
    )
      return rawAudienceTree;

    const filterSegments = (nodes?: SegmentNode[]): SegmentNode[] => {
      if (!nodes) return [];

      return nodes
        .map((node) => {
          const matchesName =
            !inputNameValue.trim() ||
            node.name?.toLowerCase().includes(inputNameValue.trim().toLowerCase());

          const matchesBrand = brand.includes("All") || brand.includes(node.brand);

          const matchesStatus = status.includes("All") || status.includes(node.status);

          const matchesCreatedBy = createdBy.includes("All") || createdBy.includes(node.createdby);

          const matchesLastEditedBy = lastEditedBy.includes("All") || lastEditedBy.includes(node.last_modified_by);

          const thisNodeMatches =
            matchesName &&
            matchesBrand &&
            matchesStatus &&
            matchesCreatedBy &&
            matchesLastEditedBy;

          const filteredChildren = filterSegments(node.children);

          if (thisNodeMatches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter(Boolean) as SegmentNode[];
    };

    return rawAudienceTree
      .map((folder1) => ({
        ...folder1,
        children: filterSegments(folder1.children),
      }))
      .filter((folder1) => folder1.children && folder1.children.length > 0);
  }, [inputNameValue, brand, status, createdBy, lastEditedBy, rawAudienceTree]);

  return {
    inputNameValue,
    status,
    createdBy,
    lastEditedBy,
    brand,
    statusOptions,
    createdByOptions,
    lastEditedByOptions,
    brandOptions,
    rawAudienceTree,
    filteredData,
  };
};